<template>
    <SortableJs
        :list="list"
        :item-key="itemKey"
        :tag="tag"
        :options="sortableOptions"
        class="relative"
        :class="[isEmpty ? 'sortable-list--empty' : '']"
        @start="start"
        @end="end"
        @add="add"
        @move.capture="handleOnMove"
    >
        <template v-if="isEmpty" #header>
            <slot name="empty">
                <div class="sortable__empty-wrapper">
                    <div class="sortable__empty-inside">
                        <span> {{ lang('fields::fields.builder.content.empty') }} </span>
                    </div>
                </div>
            </slot>
        </template>
        <template #item="{ element, index }">
            <slot name="item" :element="element" :index="index" :list-length="list?.length ?? 0"></slot>
        </template>
    </SortableJs>
</template>

<script lang="ts" setup>
import { toRefs, computed } from 'vue';
import { Sortable as SortableJs } from 'sortablejs-vue3';
import Sortable, { SortableOptions } from 'sortablejs';

interface Props {
    list: any[] | undefined;
    itemKey?: string;
    tag?: string;
    options?: any;
    group?: string;
}

const props = withDefaults(defineProps<Props>(), {
    itemKey: 'id',
    tag: 'div',
    options: {},
    group: null,
});

const emit = defineEmits<{
    (event: 'start', sortableEvent: Sortable.SortableEvent): void;
    (event: 'end', sortableEvent: Sortable.SortableEvent): void;
    (event: 'add', sortableEvent: Sortable.SortableEvent, item: any): void;
}>();

const defaults = {
    animation: 150,
    ghostClass: 'sortable-ghost',
    chosenClass: 'sortable-chosen',
    dragClass: 'sortable-drag',
    scroll: true,
    forceFallback: true,
    bubbleScroll: true,
    handle: '.cursor-grab, .forms__field--repeater__subfield__header__index',
};

const sortableOptions: SortableOptions = { ...defaults, ...props.options };

if (props.group) {
    sortableOptions.group = props.group;
}

const { list, itemKey, tag } = toRefs(props);

/**
 * Save the item data to the element dataset when drag starts.
 *
 * @param event SortableEvent
 * @return void No return expected.
 */
const start = (event: Sortable.SortableEvent) => {
    const item = list.value![event.oldIndex!];
    event.item.dataset.element = JSON.stringify(item);
    emit('start', event);
};

/**
 * Emit the end event when drag ends.
 *
 * @param event SortableEvent
 * @return void No return expected.
 */
const end = (event: Sortable.SortableEvent) => {
    emit('end', event);
};

/**
 * Emit the add event when an item is added to the list.
 *
 * @param event SortableEvent
 * @return void No return expected.
 */
const add = (event: Sortable.SortableEvent) => {
    const item = event.item.dataset.element ? JSON.parse(event.item.dataset.element) : {};
    emit('add', event, item);
};

const isEmpty = computed(() => list.value?.length === 0);

const handleOnMove = (event: Sortable.MoveEvent, event2: Event): boolean | -1 | 1 => {
    if (!sortableOptions.onMove) {
        return true;
    }

    return sortableOptions.onMove(event, event2);
};
</script>
